// Sessão de estudo (0.67, texto 05 G3): matérias na ordem, com o tempo de cada uma, e pausas opcionais entre elas.
// As sessões prontas ficam salvas (estudos.prontas) para repetir em outros dias só tocando em "Começar".
// Durante a sessão: mini temporizador, barrinha, Pausar/Próxima/Encerrar, avisos a cada troca.
// O tempo realmente estudado em cada matéria entra sozinho na meta de Estudos (pausas não contam).
// A sessão NÃO vai para o My Planner.
import { useSyncExternalStore } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { getDoc, setDoc } from '../store/store';
import { registrarSessao } from '../store/estudos';
import type { PassoSessao, PlanoSessao, SessaoPronta } from '../store/types';
import { estadoAgora, type Trecho } from './temporizador';
import { comoAvisar, modoDaAba, somDoAviso } from './avisos';
import { modNotificacoes } from '../notifications';
import { addDays, parseYmd, today, uid } from './date';
import { t } from './i18n';
import { aoVoltar } from './ausencia';

// ─── sessões prontas (sincronizam como o resto de Estudos) ───
export const sessoesProntas = (): SessaoPronta[] => getDoc('estudos').prontas ?? [];
export function salvarSessaoPronta(s: SessaoPronta) {
  setDoc('estudos', d => {
    const l = d.prontas ?? [];
    return { ...d, prontas: l.some(x => x.id === s.id) ? l.map(x => (x.id === s.id ? s : x)) : [...l, s] };
  });
}
export const excluirSessaoPronta = (id: string) => setDoc('estudos', d => ({ ...d, prontas: (d.prontas ?? []).filter(x => x.id !== id) }));
export const duplicarSessaoPronta = (s: SessaoPronta) => salvarSessaoPronta({ ...JSON.parse(JSON.stringify(s)), id: uid(), nome: s.nome + ' ' + t('(cópia)') });
export const novaSessaoPronta = (): SessaoPronta => ({ id: uid(), nome: '', passos: [] });

// ─── 0.72 (texto 11e C): plano com período (opcional; sem plano a sessão é como antes) ───
// O plano NÃO vai para o My Planner (regra fixa). O tempo de "hoje" vem do histórico de Estudos (minutosNoDia),
// o mesmo que entra na meta e no relatório: sessão e cronômetro simples contam uma vez só.
/** Plano novo: começa hoje, 30 dias, 2 h por dia, de segunda a sexta. */
export const novoPlano = (): PlanoSessao => ({ ativo: true, inicio: today(), fim: addDays(today(), 29), minDia: 120, dias: [1, 2, 3, 4, 5] });
/** Dias do plano (só os dias da semana marcados) entre o começo e a data, inclusive. */
export function diasDoPlanoAte(p: PlanoSessao, ate: string) {
  let n = 0;
  for (let d = p.inicio, i = 0; d <= ate && d <= p.fim && i < 3700; d = addDays(d, 1), i++) if (p.dias.includes(parseYmd(d).getDay())) n++;
  return n;
}
export type EstadoPlano = { fase: 'antes' | 'dia' | 'folga' | 'terminou'; dia: number; total: number };
/** Onde o plano está hoje: "Dia 12 de 90" (contando só os dias marcados), folga, antes do começo ou terminado. */
export function estadoPlano(p: PlanoSessao, hoje = today()): EstadoPlano {
  const total = diasDoPlanoAte(p, p.fim);
  if (hoje < p.inicio) return { fase: 'antes', dia: 0, total };
  if (hoje > p.fim) return { fase: 'terminou', dia: total, total };
  return { fase: p.dias.includes(parseYmd(hoje).getDay()) ? 'dia' : 'folga', dia: diasDoPlanoAte(p, hoje), total };
}
/** Sessões salvas com plano ligado e ainda não encerrado. */
export const sessoesComPlano = (l = sessoesProntas()) => l.filter(s => s.plano?.ativo && !s.plano.encerrado);
const mudarPlano = (id: string, fn: (p: PlanoSessao) => PlanoSessao) =>
  setDoc('estudos', d => ({ ...d, prontas: (d.prontas ?? []).map(s => (s.id === id && s.plano ? { ...s, plano: fn(s.plano) } : s)) }));
/** "Encerrar": o plano para de aparecer; a sessão, o plano e o histórico continuam salvos (nada é apagado). */
export const encerrarPlano = (id: string) => mudarPlano(id, p => ({ ...p, encerrado: true }));
/** "Prolongar": nova data final. */
export const prolongarPlano = (id: string, fim: string) => mudarPlano(id, p => ({ ...p, fim, encerrado: false }));

// ─── sessão em andamento (só neste aparelho; continua se o app fechar) ───
export type Andamento = {
  sid: string; nome: string; passos: PassoSessao[];   // minutos de cada passo (Próxima encurta o passo atual)
  inicioMs: number; pausadoMs: number; pausaDesde: number | null;
  creditado: Record<number, number>;                 // minutos já lançados de cada passo de matéria
  registros?: Record<string, string>;                // matéria|dia → id do registro no histórico (um só por matéria na sessão)
  avisos: string[];                                  // ids dos avisos agendados
};
const CHAVE = 'mp_sessao_estudo';
let atual: Andamento | null = null;
const ouvintes = new Set<() => void>();
const avisar = () => ouvintes.forEach(f => f());
// 0.69: a cada mudança, um objeto novo. Antes o mesmo objeto era alterado no lugar e quem usa useSessao (o cartão)
// não redesenhava na hora (ex.: o "Pausar" só mudava no próximo tique de 15 s).
const salvar = () => { if (atual) atual = { ...atual }; AsyncStorage.setItem(CHAVE, JSON.stringify(atual)).catch(() => {}); avisar(); };
AsyncStorage.getItem(CHAVE).then(s => { try { const a = s ? JSON.parse(s) as Andamento : null; if (a && !atual) { atual = a; avisar(); verificar(); } } catch { /* vazio */ } }).catch(() => {});

export const useSessao = () => useSyncExternalStore(cb => { ouvintes.add(cb); return () => { ouvintes.delete(cb); }; }, () => atual);
export const sessaoAtual = () => atual;

/** 0.69 (F2): a matéria da vez na sessão (null na pausa, sem sessão ou depois do fim). */
export function materiaDaVez(a: Andamento | null, agora = Date.now()): string | null {
  if (!a) return null;
  const e = estadoDa(a, agora);
  const p = a.passos[e.i];
  return p?.tipo === 'materia' ? p.materiaId ?? null : null;
}

// 0.69 (F2.6): "Abrir [matéria]" no aviso de troca: a tela de Estudos abre a matéria pedida (não abre sozinho).
let pedidoAbrir: { mid: string; n: number } | null = null;   // n: o mesmo pedido de novo também abre
let nPedido = 0;
const ouvPedido = new Set<() => void>();
export function pedirAbrirMateria(mid: string) { pedidoAbrir = { mid, n: ++nPedido }; ouvPedido.forEach(f => f()); }
export const usePedidoAbrir = () => useSyncExternalStore(cb => { ouvPedido.add(cb); return () => { ouvPedido.delete(cb); }; }, () => pedidoAbrir, () => null);
export const limparPedidoAbrir = () => { pedidoAbrir = null; };

const materiaDe = (mid?: string) => { if (mid) for (const f of getDoc('estudos').ficharios) { const m = f.materias.find(x => x.id === mid); if (m) return m; } return null; };
/** Os passos como trechos do temporizador (matéria = atividade). */
export const trechosDe = (passos: PassoSessao[]): Trecho[] => passos.map(p => {
  if (p.tipo === 'pausa') return { tipo: 'pausa', min: p.min, nome: t('Pausa'), emoji: '☕' };
  const m = materiaDe(p.materiaId);
  return { tipo: 'ativ', min: p.min, nome: m?.nome ?? t('Matéria'), emoji: m?.icon ?? '📚' };
});
const pausadoTotal = (a: Andamento, agora = Date.now()) => a.pausadoMs + (a.pausaDesde ? agora - a.pausaDesde : 0);
export const estadoDa = (a: Andamento, agora = Date.now()) => estadoAgora(a.inicioMs, trechosDe(a.passos), agora, pausadoTotal(a, agora));

/** Lança na meta o tempo estudado (só matérias) até agora; passos já creditados não contam duas vezes. */
function creditar(a: Andamento, agora = Date.now()) {
  const e = estadoDa(a, agora);
  let acc = 0;
  const feitoMs = agora - a.inicioMs - pausadoTotal(a, agora);
  a.passos.forEach((p, i) => {
    const dur = p.min * 60000, ini = acc; acc += dur;
    if (p.tipo !== 'materia' || !p.materiaId) return;
    const feito = Math.max(0, Math.min(dur, feitoMs - ini)) / 60000;
    const ja = a.creditado[i] ?? 0, novo = Math.floor(feito) - ja;
    if (novo > 0) {
      // o mesmo registro do histórico cresce enquanto a sessão corre ("Matemática 40 min" uma vez só)
      const regs = (a.registros ??= {}), ch = `${p.materiaId}|${today()}`;
      registrarSessao(p.materiaId, novo, (regs[ch] ??= uid()));
      a.creditado[i] = ja + novo;
    }
  });
  return e;
}

/** Avisos de cada troca (começo e fim de cada pausa, troca de matéria e fim), no modo de Ajustes > Abas > Estudos. */
async function agendarAvisos(a: Andamento) {
  const N = modNotificacoes();
  if (!N) return;
  await Promise.all(a.avisos.map(id => N.cancelScheduledNotificationAsync(id).catch(() => {})));
  a.avisos = [];
  if (a.pausaDesde) return;
  const trechos = trechosDe(a.passos);
  let acc = a.inicioMs + a.pausadoMs;
  for (let i = 0; i < trechos.length; i++) {
    acc += trechos[i].min * 60000;
    if (acc <= Date.now()) continue;
    const prox = trechos[i + 1];
    const titulo = !prox ? '🏁 ' + t('Sessão encerrada') : prox.tipo === 'pausa' ? '☕ ' + t('Hora da pausa') : `${prox.emoji ?? '📚'} ` + t('Agora: {m}', { m: prox.nome ?? '' });
    const quando = new Date(acc);
    const como = comoAvisar(quando, modoDaAba('estudos'));
    if (!como) continue;
    try {
      const nid = await N.scheduleNotificationAsync({ identifier: `sessao:${a.inicioMs}:${i}:${acc}`, content: { title: titulo, body: a.nome, sound: somDoAviso(como), data: { tipo: 'sessao' } },
        trigger: { type: N.SchedulableTriggerInputTypes.DATE, date: quando, channelId: como.canal } as any });
      a.avisos.push(nid);
    } catch { /* sem avisos */ }
  }
}

/** Tela de bloqueio: fica para uma próxima versão (precisa de app novo nas lojas). */
export function atualizarTelaBloqueio() { /* nada por enquanto */ }

export function comecarSessao(s: SessaoPronta) {
  if (!s.passos.length) return;
  if (atual) encerrarSessao();
  atual = { sid: s.id, nome: s.nome || t('Sessão de estudo'), passos: s.passos.map(p => ({ ...p })), inicioMs: Date.now(), pausadoMs: 0, pausaDesde: null, creditado: {}, avisos: [] };
  agendarAvisos(atual).finally(salvar);
  atualizarTelaBloqueio();
}
export function pausarOuContinuar() {
  const a = atual; if (!a) return;
  if (a.pausaDesde) { a.pausadoMs += Date.now() - a.pausaDesde; a.pausaDesde = null; }
  else { creditar(a); a.pausaDesde = Date.now(); }
  agendarAvisos(a).finally(salvar);
  atualizarTelaBloqueio();
}
/** Pula para o próximo passo: o atual termina agora (conta o tempo que foi estudado nele). */
export function proximoPasso() {
  const a = atual; if (!a) return;
  if (a.pausaDesde) { a.pausadoMs += Date.now() - a.pausaDesde; a.pausaDesde = null; }
  const e = creditar(a);
  if (e.i < 0 || e.i >= a.passos.length) return;
  const feitoMs = Date.now() - a.inicioMs - a.pausadoMs - a.passos.slice(0, e.i).reduce((s, p) => s + p.min * 60000, 0);
  a.passos = a.passos.map((p, i) => (i === e.i ? { ...p, min: Math.max(0, feitoMs) / 60000 } : p));
  if (e.i === a.passos.length - 1) return encerrarSessao();
  agendarAvisos(a).finally(salvar);
  atualizarTelaBloqueio();
}
/** Encerra: lança o que foi estudado e limpa. Devolve os minutos lançados. */
export function encerrarSessao(): number {
  const a = atual; if (!a) return 0;
  if (a.pausaDesde) { a.pausadoMs += Date.now() - a.pausaDesde; a.pausaDesde = null; }
  creditar(a);
  const total = Object.values(a.creditado).reduce((s, x) => s + x, 0);
  const N = modNotificacoes();
  for (const id of a.avisos) N?.cancelScheduledNotificationAsync(id).catch(() => {});
  atual = null; salvar();

  return total;
}
/** Confere se a sessão terminou sozinha (chamar de tempos em tempos e ao voltar ao app). */
export function verificar() {
  const a = atual; if (!a) return;
  const e = estadoDa(a);
  if (e.i >= a.passos.length) { encerrarSessao(); return; }
  creditar(a); salvar();
  atualizarTelaBloqueio();
}

// voltou ao app: confere se a sessão terminou, lança o tempo e atualiza a tela de bloqueio
aoVoltar(() => verificar());
